import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Modal, Input, notification, Alert } from 'antd';
import { Link } from 'react-router-dom';
import { buildAPBillingConfigPricingModelURI } from 'utils/routes';
import { MainContainer } from './PricingModel.styles';
import TableComponent from './table/Table';
import { getAllPricingModel, postPricingModel, duplicatePricingModel } from 'utils/apis/configPricingModel';
import { getConstants } from 'utils/apis/constants';
import { getHostsForBilling, getHostsWithoutParent } from 'utils/apis/host';

const PricingModel = () => {
  const [pricingModelData, setPricingModelData] = useState([]);
  const [hostData, setHostData] = useState([]);
  const [countriesData, setCountriesData] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  //Duplicate Modal
  const [isDuplicateModalVisible, setIsDuplicateModalVisible] = useState(false);
  const [duplicateSelection, setDuplicateSelection] = useState([]);
  const inputRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchData();
    fetchCountries();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await getAllPricingModel();
      setPricingModelData(response.data);

      const hosts = await getHostsForBilling();
      setHostData(hosts.data);

      // Check for hosts without a pricing model and set the alert message
      const hostsWithoutPricingModel = hosts.data.filter(host => !host.pricingModel);
      if (hostsWithoutPricingModel.length > 0) {
        setAlertMessage(`${hostsWithoutPricingModel.length} hosts do not have an associated pricing model.`);
      } else {
        setAlertMessage('');
      }
    } catch (error) {
      console.error('Error fetching Pricing Model data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCountries = () => {
    getConstants('countries').then(countriesRes => {
      if (countriesRes && countriesRes.status === 200) {
        const options = Object.keys(countriesRes.data).map(k => ({
          key: countriesRes.data[k].iso2,
          label: countriesRes.data[k].name,
          value: countriesRes.data[k].name
        }));
        setCountriesData([
          {
            key: 'global',
            label: 'Global',
            value: 'Global'
          },
          ...options
        ]);
      }
    });
  };

  const showDuplicateModal = record => {
    setDuplicateSelection(record);
    setIsDuplicateModalVisible(true);
  };

  const handleDuplicate = async () => {
    const newName = inputRef.current.input.value;
    if (newName === duplicateSelection.name) {
      setErrorMessage('The new name cannot be the same as the original.');
      return;
    } else {
      try {
        const { countryName, _id, ...rest } = duplicateSelection;

        const duplicateRecord = {
          ...rest,
          name: newName,
          host: []
        };

        await postPricingModel(duplicateRecord);
        notification.success({
          message: 'Success',
          description: 'Pricing Model has been duplicated successfully.'
        });
        setIsDuplicateModalVisible(false);
        setDuplicateSelection([]);
        fetchData();
      } catch (error) {
        console.error('Trouble having to create duplicate pricing model : ' + error);
      }
    }
  };

  const handleCancel = () => {
    setDuplicateSelection([]);
    setIsDuplicateModalVisible(false);
  };

  return (
    <>
      <MainContainer>
        <Link to={`${buildAPBillingConfigPricingModelURI()}/new`}>
          <Button style={{ marginBottom: '40px' }} type="primary">
            Create Pricing Model
          </Button>
        </Link>
        <Card>
          {alertMessage && <Alert style={{ margin: '8px 0px' }} message={alertMessage} type="warning" showIcon />}
          <TableComponent data={pricingModelData} countries={countriesData} loading={isLoading} duplicateModal={showDuplicateModal} />
        </Card>
      </MainContainer>
      <Modal
        title={`Duplicate Pricing Model - ${duplicateSelection.name}`}
        visible={isDuplicateModalVisible}
        onOk={handleDuplicate}
        onCancel={handleCancel}
        okText="Duplicate"
        cancelText="Cancel"
      >
        <p>Enter the new name for the pricing model:</p>
        <Input ref={inputRef} placeholder="New Pricing Model Name" />
        {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
      </Modal>
    </>
  );
};

export default PricingModel;
